/**
 * useOfflineSync.ts
 * Überwacht Online/Offline-Status und synchronisiert die Queue
 * automatisch sobald die Verbindung wiederhergestellt wird.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import {
  getSyncQueue,
  removeFromSyncQueue,
  clearLocalOrder,
} from '../lib/offlineStore';

export interface OfflineSyncStatus {
  isOnline: boolean;
  pendingCount: number;
  isSyncing: boolean;
  lastSyncedAt: Date | null;
}

export function useOfflineSync(onSyncComplete?: (count: number) => void) {
  const [isOnline, setIsOnline]         = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing]       = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);
  const syncLock = useRef(false);

  // Pending-Anzahl aus Queue lesen
  const refreshPendingCount = useCallback(async () => {
    const queue = await getSyncQueue();
    setPendingCount(queue.length);
  }, []);

  // Sync-Queue abarbeiten
  const runSync = useCallback(async () => {
    if (syncLock.current || !navigator.onLine) return;
    syncLock.current = true;
    setIsSyncing(true);

    try {
      const queue = await getSyncQueue();
      if (queue.length === 0) {
        setIsSyncing(false);
        syncLock.current = false;
        return;
      }

      let successCount = 0;

      for (const entry of queue) {
        try {
          if (entry.type === 'update') {
            const { error } = await supabase
              .from('orders')
              .update({
                ...entry.payload,
                // Lock aufheben & Version hochzählen
                locked_by: null,
                locked_by_name: null,
                locked_at: null,
                offline_version: (entry.payload.offline_version ?? 0) + 1,
              })
              .eq('id', entry.orderId);

            if (!error) {
              await removeFromSyncQueue(entry.id);
              await clearLocalOrder(entry.orderId);
              successCount++;
            }
          }
        } catch (e) {
          console.warn('Sync-Fehler für Eintrag', entry.id, e);
        }
      }

      setPendingCount(0);
      setLastSyncedAt(new Date());
      if (onSyncComplete && successCount > 0) onSyncComplete(successCount);

    } finally {
      setIsSyncing(false);
      syncLock.current = false;
    }
  }, [onSyncComplete]);

  // Online/Offline-Events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      runSync();
    };
    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online',  handleOnline);
    window.addEventListener('offline', handleOffline);
    refreshPendingCount();

    return () => {
      window.removeEventListener('online',  handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [runSync, refreshPendingCount]);

  return {
    isOnline,
    pendingCount,
    isSyncing,
    lastSyncedAt,
    runSync,
    refreshPendingCount,
  } satisfies OfflineSyncStatus & {
    runSync: () => Promise<void>;
    refreshPendingCount: () => Promise<void>;
  };
}
